import { useStyletron } from '@thanx/uikit/theme'
import ProtectedPageHeader from 'components/ProtectedPageHeader'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useReportPermission } from 'hooks/useReportPermission'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { I18n } from 'react-redux-i18n'
import LookerFooter from 'scenes/LookerEmbed/components/LookerFooter'
import LookerIframe from 'scenes/LookerEmbed/LookerIframe'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
import ActiveCustomersAtSelectLocationsTab from './ActiveCustomersAtSelectLocationsTab'
import AverageActiveCustomersPerLocationPerMonthTab from './AverageActiveCustomersPerLocationPerMonthTab'
import EngagedCustomersPerLocationTab from './EngagedCustomersPerLocationTab'

const t = buildTranslate('looker')
enum Tabs {
  OVERVIEW = 'overview',
  AVERAGE_MONTHLY_VALUE = 'average_monthly_value',
  ENGAGED_CUSTOMERS_PER_LOCATION = 'engaged_customers_per_location',
  ACTIVE_CUSTOMERS_AT_SELECT_LOCATIONS = 'active_customers_at_select_locations',
  AVERAGE_ACTIVE_CUSTOMERS_PER_LOCATION_PER_MONTH = 'average_active_customers_per_location_per_month',
}

const EngagementReport: React.FC = () => {
  const merchant = useCurrentMerchant()
  const [css] = useStyletron()

  const reportPermission = useReportPermission({
    rbac: 'Reports',
    legacy: 'LocationReports',
  })
  const ability = useAbility()
  // This page's page-level guard (above) resolves to the broad 'Reports'
  // subject under RBAC, so this nested check additionally gates the
  // location-specific tabs on the narrower 'LocationReports' subject.
  const canAccessLocationReports = ability.can('access', 'LocationReports')
  // Promote Insights Lab reports initiative: gates the 3 new location-scoped
  // tabs behind a single shared flag across every PR in the initiative. A
  // tab shows only when BOTH the flag is on AND the LocationReports
  // permission check passes.
  const isPromoteInsightsLabReportsEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  const showLocationReportsTabs =
    isPromoteInsightsLabReportsEnabled && canAccessLocationReports

  // The clamp that used to live in an effect here is now structural: the hash
  // is resolved against the visible set, so a tab leaving the set (the flag
  // going off, or the permission being revoked) falls back to Overview on the
  // next resolve rather than leaving activeKey pointing at a pane that is no
  // longer rendered.
  const visibleTabs = showLocationReportsTabs
    ? [
        Tabs.OVERVIEW,
        Tabs.AVERAGE_MONTHLY_VALUE,
        Tabs.ENGAGED_CUSTOMERS_PER_LOCATION,
        Tabs.ACTIVE_CUSTOMERS_AT_SELECT_LOCATIONS,
        Tabs.AVERAGE_ACTIVE_CUSTOMERS_PER_LOCATION_PER_MONTH,
      ]
    : [Tabs.OVERVIEW, Tabs.AVERAGE_MONTHLY_VALUE]

  const [activeTab, setActiveTab] = useHashTab(visibleTabs, Tabs.OVERVIEW)

  const handleSwitchTab = (tab: Tabs) => {
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  if (!merchant) return null

  const title = t('urls.dashboards/130.title')
  const overviewHeight = 2170
  const averageMonthlyValueHeight = 2430

  return (
    <>
      <Helmet>
        <title>{I18n.t(`titles./reports/engagement#${activeTab}`)}</title>
      </Helmet>
      <ProtectedPageHeader
        title={title}
        resource={reportPermission.resource}
        action={reportPermission.action}
      >
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(tab: string | null) => {
            if (!tab) return
            handleSwitchTab(tab as Tabs)
          }}
        >
          {/* The nav component itself is branched, not just its items: five
              tabs wrap onto a second row at common widths, which is what
              ScrollingTabNav exists for, while the two-tab row must stay
              byte-identical to master — so that branch keeps TabNav. */}
          {showLocationReportsTabs ? (
            <ScrollingTabNav ariaLabel={t('urls.dashboards/130.title')}>
              <TabItem
                state={Tabs.OVERVIEW}
                title={t(`urls.dashboards/130.tabs.overview`)}
              />
              <TabItem
                state={Tabs.AVERAGE_MONTHLY_VALUE}
                title={t(`urls.dashboards/130.tabs.average_monthly_value`)}
              />
              <TabItem
                state={Tabs.ENGAGED_CUSTOMERS_PER_LOCATION}
                title={t('urls.dashboards/478.title')}
              />
              <TabItem
                state={Tabs.ACTIVE_CUSTOMERS_AT_SELECT_LOCATIONS}
                title={t('urls.dashboards/482.title')}
              />
              <TabItem
                state={Tabs.AVERAGE_ACTIVE_CUSTOMERS_PER_LOCATION_PER_MONTH}
                title={t('urls.dashboards/306.title')}
              />
            </ScrollingTabNav>
          ) : (
            <TabNav>
              <TabItem
                state={Tabs.OVERVIEW}
                title={t(`urls.dashboards/130.tabs.overview`)}
              />
              <TabItem
                state={Tabs.AVERAGE_MONTHLY_VALUE}
                title={t(`urls.dashboards/130.tabs.average_monthly_value`)}
              />
            </TabNav>
          )}
          <Tab.Content className="pb-l h-100 grey-05-bkg">
            <TabPane state={Tabs.OVERVIEW}>
              <Container
                className={`px-0 ${css({
                  minHeight: `${overviewHeight + 300}px`,
                })}`}
              >
                <LookerIframe
                  path={'dashboards/130'}
                  merchant={merchant}
                  height={`${overviewHeight}px`}
                />
                <LookerFooter className="my-xl" />
              </Container>
            </TabPane>
            <TabPane state={Tabs.AVERAGE_MONTHLY_VALUE}>
              <Container
                className={`px-0 ${css({
                  minHeight: `${averageMonthlyValueHeight + 300}px`,
                })}`}
              >
                <LookerIframe
                  path={'dashboards/133'}
                  merchant={merchant}
                  height={`${averageMonthlyValueHeight}px`}
                />
                <LookerFooter className="my-xl" />
              </Container>
            </TabPane>
            {showLocationReportsTabs && (
              <TabPane state={Tabs.ENGAGED_CUSTOMERS_PER_LOCATION} mountOnEnter>
                <EngagedCustomersPerLocationTab />
              </TabPane>
            )}
            {showLocationReportsTabs && (
              <TabPane
                state={Tabs.ACTIVE_CUSTOMERS_AT_SELECT_LOCATIONS}
                mountOnEnter
              >
                <ActiveCustomersAtSelectLocationsTab />
              </TabPane>
            )}
            {showLocationReportsTabs && (
              <TabPane
                state={Tabs.AVERAGE_ACTIVE_CUSTOMERS_PER_LOCATION_PER_MONTH}
                mountOnEnter
              >
                <AverageActiveCustomersPerLocationPerMonthTab />
              </TabPane>
            )}
          </Tab.Content>
        </Tab.Container>
      </ProtectedPageHeader>
    </>
  )
}

export default EngagementReport
